<template>
  <div class="fixed inset-0 z-50 bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 overflow-y-auto">
    <!-- Header -->
    <div class="bg-white/90 dark:bg-slate-800/90 shadow-sm sticky top-0 z-10 border-b border-slate-200/50 dark:border-slate-700/50">
      <div class="max-w-5xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
        <!-- Title Row -->
        <div class="flex items-center justify-between mb-3">
          <h1 class="text-lg sm:text-xl font-bold text-slate-800 dark:text-white">Admin Dashboard</h1>
          <button @click="$emit('close')" class="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors">
            <X class="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>
        <!-- Scrollable Tabs -->
        <div class="-mx-3 sm:-mx-4 px-3 sm:px-4 overflow-x-auto scrollbar-hide">
          <div class="flex gap-1.5 sm:gap-2 min-w-max pb-1">
            <button
              @click="activeTab = 'sources'"
              class="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-xl transition-colors whitespace-nowrap"
              :class="activeTab === 'sources' 
                ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 shadow-md' 
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50'"
            >
              📁 Quellen
            </button>
            <button
              @click="activeTab = 'sync'; fetchSyncDashboard()"
              class="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-xl transition-colors whitespace-nowrap"
              :class="activeTab === 'sync' 
                ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 shadow-md' 
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50'"
            >
              🔄 Sync
            </button>
            <button
              @click="activeTab = 'callqueues'; fetchCallQueues()"
              class="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-xl transition-colors whitespace-nowrap"
              :class="activeTab === 'callqueues' 
                ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 shadow-md' 
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50'"
            >
              📞 Queues
            </button>
            <button
              @click="activeTab = 'duplicates'; fetchDuplicates()"
              class="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-xl transition-colors whitespace-nowrap"
              :class="activeTab === 'duplicates' 
                ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 shadow-md' 
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50'"
            >
              👥 Duplikate
            </button>
            <button
              @click="activeTab = 'debug'"
              class="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-xl transition-colors whitespace-nowrap"
              :class="activeTab === 'debug' 
                ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 shadow-md' 
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50'"
            >
              🔍 Debug
            </button>
            <button
              @click="activeTab = 'logs'; fetchLogs()"
              class="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-xl transition-colors whitespace-nowrap"
              :class="activeTab === 'logs' 
                ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 shadow-md' 
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50'"
            >
              📋 Logs
            </button>
            <button
              @click="activeTab = 'maintenance'; fetchBackfillSettings(); fetchBackfillHistory()"
              class="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-xl transition-colors whitespace-nowrap"
              :class="activeTab === 'maintenance' 
                ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 shadow-md' 
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700/50'"
            >
              🔧 Wartung
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
      <!-- Sources Tab -->
      <template v-if="activeTab === 'sources'">
        <!-- Sync Actions -->
        <div class="bg-white/90 dark:bg-slate-800/90 rounded-2xl shadow-sm border border-white/20 dark:border-slate-700/50 p-4 sm:p-6">
          <h2 class="text-base sm:text-lg font-semibold text-slate-800 dark:text-white mb-3 sm:mb-4">Synchronisation</h2>
          <div class="flex gap-3 sm:gap-4">
            <button
              @click="syncAll"
              :disabled="syncing"
              class="px-3 sm:px-4 py-2 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 disabled:bg-slate-400 text-white rounded-xl flex items-center gap-2 transition-colors text-sm sm:text-base"
            >
              <RefreshCw class="w-4 h-4" :class="{ 'animate-spin': syncing }" />
              <span class="hidden sm:inline">Alle Quellen synchronisieren</span>
              <span class="sm:hidden">Sync All</span>
            </button>
          </div>
        </div>

        <!-- Contact Sources -->
        <div class="bg-white/90 dark:bg-slate-800/90 rounded-2xl shadow-sm border border-white/20 dark:border-slate-700/50 p-4 sm:p-6">
          <div class="flex items-center justify-between mb-3 sm:mb-4">
            <h2 class="text-base sm:text-lg font-semibold text-slate-800 dark:text-white">Kontaktquellen</h2>
            <button
              @click="showAddSource = true"
              class="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white rounded-xl flex items-center gap-1 text-sm transition-colors"
            >
              <Plus class="w-4 h-4" />
              <span class="hidden sm:inline">Quelle hinzufügen</span>
              <span class="sm:hidden">Neu</span>
            </button>
          </div>

          <div class="space-y-3 sm:space-y-4">
            <div
              v-for="source in sources"
              :key="source.id"
              class="border border-slate-200/50 dark:border-slate-700/50 rounded-xl p-3 sm:p-4 bg-white/50 dark:bg-slate-800/50"
            >
              <div class="flex items-start justify-between">
                <div>
                  <div class="flex items-center gap-2">
                    <h3 class="font-medium text-slate-800 dark:text-white">{{ source.display_name }}</h3>
                    <span 
                      class="px-2 py-0.5 text-xs rounded-full"
                      :class="source.is_active ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'"
                    >
                      {{ source.is_active ? 'Aktiv' : 'Inaktiv' }}
                    </span>
                  </div>
                  <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {{ source.source_type === 'entra_id' ? 'Entra ID Users' : source.source_type === 'personal_contacts' ? `Eigene Kontakte: ${source.source_identifier}` : `Shared Mailbox: ${source.source_identifier}` }}
                  </p>
                  <p class="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    {{ source.contact_count }} Kontakte • 
                    Letzter Sync: {{ source.last_sync_at ? formatDate(source.last_sync_at) : 'Nie' }}
                    <span v-if="source.last_sync_status === 'error'" class="text-red-500">
                      (Fehler: {{ source.last_sync_error }})
                    </span>
                  </p>
                </div>
                <div class="flex gap-2">
                  <button
                    @click="syncSource(source.id)"
                    class="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-500 hover:bg-slate-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                    title="Synchronisieren"
                  >
                    <RefreshCw class="w-4 h-4" />
                  </button>
                  <button
                    v-if="source.id !== 1"
                    @click="askDeleteSource(source)"
                    class="p-2 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    title="Löschen"
                  >
                    <Trash2 class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Debug Tab -->
      <template v-if="activeTab === 'debug'">
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div class="mb-4">
            <h2 class="text-lg font-semibold text-slate-800 dark:text-white mb-2">Benutzer-Debug</h2>
            <p class="text-sm text-slate-500 dark:text-slate-400">
              Suche nach einem Benutzer um alle Daten zu sehen (Kontakt, Anrufe, Call Queue Mitgliedschaft).
            </p>
          </div>

          <!-- Search Input -->
          <div class="relative mb-6">
            <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              v-model="debugSearch"
              type="text"
              placeholder="Name, E-Mail oder Telefonnummer..."
              class="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              @input="debouncedDebugSearch"
              @keydown.enter="searchDebugUser"
            >
          </div>

          <!-- Loading State -->
          <div v-if="debugLoading" class="text-center py-8">
            <RefreshCw class="w-6 h-6 mx-auto text-slate-400 animate-spin" />
            <p class="text-sm text-slate-500 mt-2">Suche...</p>
          </div>

          <!-- No Results -->
          <div v-else-if="debugSearched && !debugSearchResults.length && !debugUser" class="text-center py-8 text-slate-400">
            <Search class="w-8 h-8 mx-auto mb-2" />
            Kein Benutzer gefunden
          </div>

          <!-- Search Results List (when multiple matches) -->
          <div v-else-if="debugSearchResults.length > 0 && !debugUser" class="space-y-2 mb-6">
            <p class="text-sm text-slate-500 mb-3">{{ debugSearchResults.length }} Ergebnis(se) gefunden - Klicke auf einen Eintrag für Details:</p>
            <div 
              v-for="result in debugSearchResults" 
              :key="result.email"
              @click="selectDebugContact(result.email)"
              class="p-3 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
                       :class="result.is_entra_user ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'">
                    {{ result.is_entra_user ? '👤' : '📇' }}
                  </div>
                  <div>
                    <div class="font-medium text-slate-800 dark:text-white">{{ result.display_name }}</div>
                    <div class="text-xs text-slate-500">{{ result.email }}</div>
                  </div>
                </div>
                <div class="text-right">
                  <span v-if="result.is_entra_user" class="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 rounded-full">Entra ID</span>
                  <span v-else class="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 rounded-full">Telefonbuch</span>
                  <div class="text-xs text-slate-400 mt-1">{{ result.source_names?.join(', ') }}</div>
                </div>
              </div>
              <div v-if="result.department || result.company_name" class="text-xs text-slate-500 mt-1 ml-11">
                {{ [result.job_title, result.department, result.company_name].filter(Boolean).join(' · ') }}
              </div>
            </div>
          </div>

          <!-- Selected User Details -->
          <div v-else-if="debugUser" class="space-y-6">
            <!-- Back Button -->
            <button 
              v-if="debugSearchResults.length > 1"
              @click="debugUser = null" 
              class="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 mb-4"
            >
              ← Zurück zur Liste
            </button>

            <!-- Contact Info Card -->
            <div class="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <div class="bg-slate-50 dark:bg-slate-700/50 px-4 py-2 border-b border-slate-200 dark:border-slate-700">
                <h3 class="font-medium text-slate-800 dark:text-white flex items-center gap-2">
                  <Users class="w-4 h-4" />
                  Kontakt-Daten
                </h3>
              </div>
              <div class="p-4 grid grid-cols-2 gap-3 text-sm">
                <div><span class="text-slate-500">Name:</span> <span class="text-slate-800 dark:text-white font-medium">{{ debugUser.contact?.display_name || '-' }}</span></div>
                <div><span class="text-slate-500">E-Mail:</span> <span class="text-slate-800 dark:text-white">{{ debugUser.contact?.email || '-' }}</span></div>
                <div><span class="text-slate-500">Entra ID:</span> <code class="text-xs bg-slate-100 dark:bg-slate-800 px-1 rounded">{{ debugUser.contact?.entra_id || '-' }}</code></div>
                <div><span class="text-slate-500">Abteilung:</span> <span class="text-slate-800 dark:text-white">{{ debugUser.contact?.department || '-' }}</span></div>
                <div><span class="text-slate-500">Firma:</span> <span class="text-slate-800 dark:text-white">{{ debugUser.contact?.company_name || '-' }}</span></div>
                <div><span class="text-slate-500">Position:</span> <span class="text-slate-800 dark:text-white">{{ debugUser.contact?.job_title || '-' }}</span></div>
                <div><span class="text-slate-500">Mobil:</span> <span class="text-slate-800 dark:text-white">{{ debugUser.contact?.mobile_phone || '-' }}</span></div>
                <div><span class="text-slate-500">Telefon:</span> <span class="text-slate-800 dark:text-white">{{ formatDebugBusinessPhones(debugUser.contact?.business_phones) }}</span></div>
                <div><span class="text-slate-500">Entra User:</span> 
                  <span :class="debugUser.contact?.is_entra_user ? 'text-green-600' : 'text-slate-400'">
                    {{ debugUser.contact?.is_entra_user ? '✓ Ja' : '✗ Nein' }}
                  </span>
                </div>
                <div><span class="text-slate-500">Quellen:</span> <span class="text-slate-800 dark:text-white">{{ debugUser.contact?.source_ids?.join(', ') || '-' }}</span></div>
              </div>
            </div>

            <!-- Presence Card -->
            <div v-if="debugUser.presence" class="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <div class="bg-slate-50 dark:bg-slate-700/50 px-4 py-2 border-b border-slate-200 dark:border-slate-700">
                <h3 class="font-medium text-slate-800 dark:text-white flex items-center gap-2">
                  <div class="w-3 h-3 rounded-full" :class="getDebugPresenceClass(debugUser.presence.availability)"></div>
                  Teams Präsenz
                </h3>
              </div>
              <div class="p-4 grid grid-cols-2 gap-3 text-sm">
                <div><span class="text-slate-500">Status:</span> <span class="text-slate-800 dark:text-white">{{ debugUser.presence.availability }}</span></div>
                <div><span class="text-slate-500">Aktivität:</span> <span class="text-slate-800 dark:text-white">{{ debugUser.presence.activity || '-' }}</span></div>
                <div><span class="text-slate-500">Letzte Aktualisierung:</span> <span class="text-slate-800 dark:text-white">{{ formatDate(debugUser.presence.updated_at) }}</span></div>
              </div>
            </div>

            <!-- Call Queue Memberships -->
            <div class="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <div class="bg-slate-50 dark:bg-slate-700/50 px-4 py-2 border-b border-slate-200 dark:border-slate-700">
                <h3 class="font-medium text-slate-800 dark:text-white flex items-center gap-2">
                  <Phone class="w-4 h-4" />
                  Call Queue Mitgliedschaften ({{ debugUser.callQueueMemberships?.length || 0 }})
                </h3>
              </div>
              <div v-if="debugUser.callQueueMemberships?.length > 0" class="divide-y divide-slate-100 dark:divide-slate-700">
                <div v-for="cq in debugUser.callQueueMemberships" :key="cq.queue_user_id" class="px-4 py-2 text-sm flex items-center justify-between">
                  <span class="text-slate-800 dark:text-white">{{ cq.queue_name }}</span>
                  <span class="text-slate-500">{{ cq.call_count }} Anrufe</span>
                </div>
              </div>
              <div v-else class="p-4 text-sm text-slate-400 text-center">Keine Call Queue Mitgliedschaft</div>
            </div>

            <!-- Recent Calls -->
            <div class="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <div class="bg-slate-50 dark:bg-slate-700/50 px-4 py-2 border-b border-slate-200 dark:border-slate-700">
                <h3 class="font-medium text-slate-800 dark:text-white flex items-center gap-2">
                  <PhoneCall class="w-4 h-4" />
                  Letzte Anrufe ({{ debugUser.recentCalls?.length || 0 }})
                </h3>
              </div>
              <div v-if="debugUser.recentCalls?.length > 0" class="divide-y divide-slate-100 dark:divide-slate-700 max-h-64 overflow-y-auto">
                <div v-for="call in debugUser.recentCalls" :key="call.id" class="px-4 py-2 text-sm">
                  <div class="flex items-center justify-between">
                    <span class="text-slate-800 dark:text-white">
                      {{ call.direction === 'inbound' ? '📥' : '📤' }}
                      {{ call.organizer_name || call.external_phone || 'Unbekannt' }}
                    </span>
                    <span class="text-slate-400 text-xs">{{ formatDate(call.start_time) }}</span>
                  </div>
                  <div class="text-xs text-slate-500 mt-0.5">
                    {{ call.duration_seconds }}s · {{ call.call_type }}
                    <span v-if="call.answered_by_name" class="ml-2">→ {{ call.answered_by_name }}</span>
                  </div>
                </div>
              </div>
              <div v-else class="p-4 text-sm text-slate-400 text-center">Keine Anrufe gefunden</div>
            </div>

            <!-- Phone Lookup Entries -->
            <div class="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <div class="bg-slate-50 dark:bg-slate-700/50 px-4 py-2 border-b border-slate-200 dark:border-slate-700">
                <h3 class="font-medium text-slate-800 dark:text-white flex items-center gap-2">
                  📞 Phone Lookup Einträge ({{ debugUser.phoneLookupEntries?.length || 0 }})
                </h3>
              </div>
              <div v-if="debugUser.phoneLookupEntries?.length > 0" class="divide-y divide-slate-100 dark:divide-slate-700">
                <div v-for="entry in debugUser.phoneLookupEntries" :key="entry.phone_normalized" class="px-4 py-2 text-sm flex items-center justify-between">
                  <code class="text-xs bg-slate-100 dark:bg-slate-800 px-1 rounded">{{ entry.phone_normalized }}</code>
                  <span class="text-slate-500">{{ entry.source_type }}</span>
                </div>
              </div>
              <div v-else class="p-4 text-sm text-slate-400 text-center">Keine Telefonnummern indexiert</div>
            </div>
          </div>
        </div>
      </template>

      <!-- Logs Tab -->
      <template v-if="activeTab === 'logs'">
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-slate-800 dark:text-white">System Logs</h2>
            <div class="flex gap-2">
              <button
                @click="fetchLogs"
                class="px-3 py-1.5 text-slate-600 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg flex items-center gap-1 text-sm transition-colors"
              >
                <RefreshCw class="w-4 h-4" :class="{ 'animate-spin': logsLoading }" />
                Aktualisieren
              </button>
              <button
                @click="askClearLogs"
                class="px-3 py-1.5 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg flex items-center gap-1 text-sm transition-colors"
              >
                <Trash2 class="w-4 h-4" />
                Löschen
              </button>
            </div>
          </div>

          <!-- Filters -->
          <div class="flex flex-wrap gap-3 mb-4">
            <div class="relative flex-1 min-w-48">
              <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                v-model="logSearch"
                type="text"
                placeholder="Logs durchsuchen..."
                class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                @input="debouncedFetchLogs"
              >
            </div>
            <select
              v-model="logLevel"
              class="px-4 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-slate-200"
              @change="fetchLogs"
            >
              <option value="">Alle Level</option>
              <option value="info">Info</option>
              <option value="success">Success</option>
              <option value="sync">Sync</option>
              <option value="warn">Warning</option>
              <option value="error">Error</option>
            </select>
          </div>

          <!-- Logs List -->
          <div class="space-y-2 max-h-96 overflow-y-auto">
            <div v-if="logs.length === 0" class="text-center text-slate-400 dark:text-slate-500 py-8">
              Keine Logs vorhanden
            </div>
            <div
              v-for="log in logs"
              :key="log.id"
              class="flex items-start gap-3 p-3 rounded-lg border border-slate-100 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800/50"
            >
              <!-- Level Badge -->
              <span
                class="px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0"
                :class="{
                  'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300': log.level === 'info',
                  'bg-green-100 dark:bg-green-900/50 text-slate-800 dark:text-green-300': log.level === 'success' || log.level === 'sync',
                  'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300': log.level === 'warn',
                  'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300': log.level === 'error'
                }"
              >
                {{ log.level.toUpperCase() }}
              </span>
              <!-- Content -->
              <div class="flex-1 min-w-0">
                <p class="text-sm text-slate-700 dark:text-slate-200">{{ log.message }}</p>
                <p class="text-xs text-slate-400 dark:text-slate-500 mt-1">
                  {{ formatLogDate(log.timestamp) }} • {{ log.category }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Maintenance Tab -->
      <template v-if="activeTab === 'maintenance'">
        <div class="space-y-6">
          <!-- Call History Repair -->
          <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
            <h2 class="text-lg font-semibold text-slate-800 dark:text-white mb-4">📞 Anrufhistorie reparieren</h2>
            <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Repariert fehlende "Angenommen von" Informationen und erkennt weitergeleitete Anrufe.
              Nur Anrufe der letzten 30 Tage können repariert werden (Graph API Limitation).
            </p>
            
            <div class="flex flex-wrap items-end gap-4 mb-4">
              <div>
                <label class="block text-xs text-slate-500 dark:text-slate-400 mb-1">Max. Anzahl</label>
                <input
                  v-model.number="repairOptions.limit"
                  type="number"
                  min="1"
                  max="200"
                  class="w-24 px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-600 rounded-lg text-sm"
                >
              </div>
              <div>
                <label class="block text-xs text-slate-500 dark:text-slate-400 mb-1">Min. Dauer (Sek.)</label>
                <input
                  v-model.number="repairOptions.minDurationSeconds"
                  type="number"
                  min="0"
                  max="300"
                  class="w-24 px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-600 rounded-lg text-sm"
                >
              </div>
              <div class="flex items-center gap-2">
                <input
                  id="repairTransfers"
                  v-model="repairOptions.repairTransfers"
                  type="checkbox"
                  class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                >
                <label for="repairTransfers" class="text-sm text-slate-600 dark:text-slate-400">
                  Weiterleitungen erkennen
                </label>
              </div>
            </div>
            
            <div class="flex gap-2">
              <button
                @click="runRepair(true)"
                :disabled="repairLoading"
                class="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Search class="w-4 h-4" />
                Vorschau (Dry Run)
              </button>
              <button
                @click="runRepair(false)"
                :disabled="repairLoading"
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <RefreshCw class="w-4 h-4" :class="{ 'animate-spin': repairLoading }" />
                Reparatur starten
              </button>
            </div>
            
            <!-- Repair Results -->
            <div v-if="repairResult" class="mt-4 p-4 rounded-lg bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700">
              <div class="flex items-center gap-3 mb-3">
                <CheckCircle v-if="repairResult.success" class="w-5 h-5 text-green-500" />
                <AlertCircle v-else class="w-5 h-5 text-red-500" />
                <span class="font-medium text-slate-800 dark:text-slate-200">
                  {{ repairResult.dryRun ? 'Vorschau' : 'Reparatur abgeschlossen' }}
                </span>
              </div>
              
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div class="text-center p-2 rounded bg-white dark:bg-slate-800">
                  <div class="text-xl font-bold text-slate-800 dark:text-white">{{ repairResult.total || repairResult.callsFound || 0 }}</div>
                  <div class="text-xs text-slate-500">Gefunden</div>
                </div>
                <div class="text-center p-2 rounded bg-green-50 dark:bg-green-900/20">
                  <div class="text-xl font-bold text-green-600 dark:text-green-400">{{ repairResult.repaired || 0 }}</div>
                  <div class="text-xs text-slate-500">Repariert</div>
                </div>
                <div class="text-center p-2 rounded bg-purple-50 dark:bg-purple-900/20">
                  <div class="text-xl font-bold text-purple-600 dark:text-purple-400">{{ repairResult.transfersDetected || 0 }}</div>
                  <div class="text-xs text-slate-500">Weiterleitungen</div>
                </div>
                <div class="text-center p-2 rounded bg-red-50 dark:bg-red-900/20">
                  <div class="text-xl font-bold text-red-600 dark:text-red-400">{{ repairResult.failed || 0 }}</div>
                  <div class="text-xs text-slate-500">Fehlgeschlagen</div>
                </div>
              </div>
              
              <!-- Details (expandable) -->
              <details v-if="repairResult.details?.length || repairResult.calls?.length" class="text-sm">
                <summary class="cursor-pointer text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200">
                  Details anzeigen ({{ repairResult.details?.length || repairResult.calls?.length }} Einträge)
                </summary>
                <div class="mt-2 max-h-60 overflow-y-auto space-y-1">
                  <div
                    v-for="(item, index) in (repairResult.details || repairResult.calls)"
                    :key="index"
                    class="flex items-center gap-2 p-2 rounded bg-white dark:bg-slate-800 text-xs"
                  >
                    <span
                      class="w-2 h-2 rounded-full"
                      :class="{
                        'bg-green-500': item.status === 'repaired',
                        'bg-amber-500': item.status === 'skipped' || item.status === 'no_answerer',
                        'bg-red-500': item.status === 'failed'
                      }"
                    ></span>
                    <span class="font-mono text-slate-500">{{ item.id.substring(0, 8) }}...</span>
                    <span v-if="item.answeredBy" class="text-green-600 dark:text-green-400">
                      → {{ item.answeredBy }}
                    </span>
                    <span v-if="item.isTransferred" class="text-purple-600 dark:text-purple-400">
                      📞 Weitergeleitet von {{ item.transferredBy }}
                    </span>
                    <span v-if="item.reason" class="text-amber-600 dark:text-amber-400">
                      {{ item.reason }}
                    </span>
                    <span v-if="item.error" class="text-red-600 dark:text-red-400">
                      {{ item.error }}
                    </span>
                    <span v-if="item.organizer" class="text-slate-400">
                      ({{ item.organizer }})
                    </span>
                  </div>
                </div>
              </details>
            </div>
          </div>
          
          <!-- Auto-Backfill Settings -->
          <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
            <h2 class="text-lg font-semibold text-slate-800 dark:text-white mb-4">🔄 Automatisches Backfill</h2>
            <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Wenn aktiviert, werden täglich automatisch fehlende Anrufe vom Vortag aus der Graph API nachgeladen.
            </p>
            
            <div class="flex items-center gap-4 mb-4">
              <button
                @click="toggleAutoBackfill"
                :disabled="autoBackfillLoading"
                class="flex items-center gap-2 transition-colors"
              >
                <ToggleRight v-if="autoBackfillSettings.auto_enabled" class="w-8 h-8 text-green-500" />
                <ToggleLeft v-else class="w-8 h-8 text-slate-400" />
                <span class="text-sm font-medium" :class="autoBackfillSettings.auto_enabled ? 'text-green-600 dark:text-green-400' : 'text-slate-500 dark:text-slate-400'">
                  {{ autoBackfillSettings.auto_enabled ? 'Aktiviert' : 'Deaktiviert' }}
                </span>
              </button>
              
              <div v-if="autoBackfillSettings.auto_enabled" class="flex items-center gap-2">
                <label class="text-xs text-slate-500 dark:text-slate-400">Uhrzeit:</label>
                <select
                  v-model.number="autoBackfillSettings.run_hour"
                  @change="saveAutoBackfillSettings"
                  class="px-2 py-1 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-600 rounded-lg text-sm"
                >
                  <option v-for="h in 24" :key="h" :value="h-1">{{ String(h-1).padStart(2, '0') }}:00 Uhr</option>
                </select>
              </div>
            </div>

            <!-- Backfill History -->
            <div v-if="backfillHistory.length > 0">
              <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                <History class="w-4 h-4" />
                Backfill-Verlauf
              </h3>
              <div class="max-h-60 overflow-y-auto">
                <table class="w-full text-xs">
                  <thead>
                    <tr class="border-b border-slate-200 dark:border-slate-700 text-left">
                      <th class="py-1.5 px-2 font-medium text-slate-500 dark:text-slate-400">Datum</th>
                      <th class="py-1.5 px-2 font-medium text-slate-500 dark:text-slate-400">Typ</th>
                      <th class="py-1.5 px-2 font-medium text-slate-500 dark:text-slate-400 text-right">Graph</th>
                      <th class="py-1.5 px-2 font-medium text-slate-500 dark:text-slate-400 text-right">Fehlend</th>
                      <th class="py-1.5 px-2 font-medium text-slate-500 dark:text-slate-400 text-right">Nachgeladen</th>
                      <th class="py-1.5 px-2 font-medium text-slate-500 dark:text-slate-400 text-right">Fehler</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="entry in backfillHistory"
                      :key="entry.id"
                      class="border-b border-slate-100 dark:border-slate-700/50"
                    >
                      <td class="py-1.5 px-2 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                        {{ formatDateTimeFull(entry.created_at) }}
                      </td>
                      <td class="py-1.5 px-2">
                        <span
                          class="px-1.5 py-0.5 rounded text-[10px] font-bold"
                          :class="entry.trigger_type === 'auto'
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'"
                        >
                          {{ entry.trigger_type === 'auto' ? 'Auto' : entry.dry_run ? 'Vorschau' : 'Manuell' }}
                        </span>
                      </td>
                      <td class="py-1.5 px-2 text-right text-slate-600 dark:text-slate-300">{{ entry.total_in_graph }}</td>
                      <td class="py-1.5 px-2 text-right">
                        <span :class="entry.missing > 0 ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-slate-400'">{{ entry.missing }}</span>
                      </td>
                      <td class="py-1.5 px-2 text-right">
                        <span :class="entry.processed > 0 ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-slate-400'">{{ entry.processed }}</span>
                      </td>
                      <td class="py-1.5 px-2 text-right">
                        <span v-if="entry.error_message" class="text-red-500" :title="entry.error_message">!</span>
                        <span v-else :class="entry.failed > 0 ? 'text-red-500 font-semibold' : 'text-slate-400'">{{ entry.failed }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <p v-else-if="!backfillHistoryLoading" class="text-xs text-slate-400 dark:text-slate-500 italic">Noch kein Backfill-Verlauf vorhanden.</p>
          </div>
          
          <!-- Call History Backfill (Manual) -->
          <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
            <h2 class="text-lg font-semibold text-slate-800 dark:text-white mb-4">📥 Manuelles Backfill</h2>
            <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Lädt fehlende Anrufe aus der Microsoft Graph API nach. Nützlich wenn Webhook-Verarbeitung fehlgeschlagen ist.
              Nur die letzten 30 Tage sind abrufbar (Graph API Limitation).
            </p>
            
            <div class="flex flex-wrap items-end gap-4 mb-4">
              <div>
                <label class="block text-xs text-slate-500 dark:text-slate-400 mb-1">Von Datum</label>
                <input
                  v-model="backfillOptions.startDate"
                  type="date"
                  :max="backfillOptions.endDate || today"
                  class="px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-600 rounded-lg text-sm"
                >
              </div>
              <div>
                <label class="block text-xs text-slate-500 dark:text-slate-400 mb-1">Bis Datum</label>
                <input
                  v-model="backfillOptions.endDate"
                  type="date"
                  :min="backfillOptions.startDate"
                  :max="today"
                  class="px-3 py-2 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-600 rounded-lg text-sm"
                >
              </div>
            </div>
            
            <div class="flex gap-2">
              <button
                @click="runBackfill(true)"
                :disabled="backfillLoading || !backfillOptions.startDate"
                class="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Search class="w-4 h-4" />
                Vorschau (Dry Run)
              </button>
              <button
                @click="runBackfill(false)"
                :disabled="backfillLoading || !backfillOptions.startDate"
                class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Download class="w-4 h-4" :class="{ 'animate-bounce': backfillLoading }" />
                Backfill starten
              </button>
            </div>
            
            <!-- Backfill Results -->
            <div v-if="backfillResult" class="mt-4 p-4 rounded-lg bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700">
              <div class="flex items-center gap-3 mb-3">
                <CheckCircle v-if="backfillResult.success" class="w-5 h-5 text-green-500" />
                <AlertCircle v-else class="w-5 h-5 text-red-500" />
                <span class="font-medium text-slate-800 dark:text-slate-200">
                  {{ backfillResult.dryRun ? 'Vorschau' : 'Backfill abgeschlossen' }}
                </span>
              </div>
              
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div class="text-center p-2 rounded bg-white dark:bg-slate-800">
                  <div class="text-xl font-bold text-slate-800 dark:text-white">{{ backfillResult.totalInGraph || 0 }}</div>
                  <div class="text-xs text-slate-500">In Graph API</div>
                </div>
                <div class="text-center p-2 rounded bg-blue-50 dark:bg-blue-900/20">
                  <div class="text-xl font-bold text-blue-600 dark:text-blue-400">{{ backfillResult.alreadyInDb || 0 }}</div>
                  <div class="text-xs text-slate-500">Bereits in DB</div>
                </div>
                <div class="text-center p-2 rounded bg-amber-50 dark:bg-amber-900/20">
                  <div class="text-xl font-bold text-amber-600 dark:text-amber-400">{{ backfillResult.missing || 0 }}</div>
                  <div class="text-xs text-slate-500">Fehlend</div>
                </div>
                <div class="text-center p-2 rounded bg-green-50 dark:bg-green-900/20">
                  <div class="text-xl font-bold text-green-600 dark:text-green-400">{{ backfillResult.processed || 0 }}</div>
                  <div class="text-xs text-slate-500">Nachgeladen</div>
                </div>
              </div>
              
              <!-- Details (expandable) -->
              <details v-if="backfillResult.details?.length || backfillResult.missingIds?.length" class="text-sm">
                <summary class="cursor-pointer text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200">
                  Details anzeigen ({{ backfillResult.details?.length || backfillResult.missingIds?.length }} Einträge)
                </summary>
                <div class="mt-2 max-h-60 overflow-y-auto space-y-1">
                  <div
                    v-for="(item, index) in (backfillResult.details || backfillResult.missingIds)"
                    :key="index"
                    class="flex items-center gap-2 p-2 rounded bg-white dark:bg-slate-800 text-xs"
                  >
                    <span
                      class="w-2 h-2 rounded-full"
                      :class="{
                        'bg-green-500': item.status === 'processed',
                        'bg-amber-500': !item.status,
                        'bg-red-500': item.status === 'failed'
                      }"
                    ></span>
                    <span class="font-mono text-slate-500">{{ item.id.substring(0, 8) }}...</span>
                    <span class="text-slate-400">{{ item.type }}</span>
                    <span v-if="item.startTime || item.startDateTime" class="text-slate-400">
                      {{ formatDateTime(item.startTime || item.startDateTime) }}
                    </span>
                    <span v-if="item.error" class="text-red-600 dark:text-red-400">
                      {{ item.error }}
                    </span>
                  </div>
                </div>
              </details>
            </div>
          </div>
          
          <!-- Info Box -->
          <div class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
            <div class="flex gap-3">
              <Info class="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div class="text-sm text-blue-800 dark:text-blue-200">
                <strong>Weiterleitungen erkennen:</strong> Wenn ein interner Mitarbeiter einen Anruf an eine Call Queue weiterleitet, 
                erscheint dessen Telefonnummer als "Anrufer". Diese Funktion erkennt solche Fälle und markiert sie als 
                "Weitergeleitet von [Name]" statt einen falschen externen Kontakt anzuzeigen.
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Sync-Status Tab -->
      <template v-if="activeTab === 'sync'">
        <!-- Statistics Cards -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4">
            <div class="text-2xl font-bold text-slate-800 dark:text-white">{{ syncDashboard.stats?.total_contacts || 0 }}</div>
            <div class="text-sm text-slate-500 dark:text-slate-400">Gesamt Kontakte</div>
          </div>
          <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4">
            <div class="text-2xl font-bold text-blue-600 dark:text-blue-400">{{ syncDashboard.stats?.entra_contacts || 0 }}</div>
            <div class="text-sm text-slate-500 dark:text-slate-400">Entra ID Users</div>
          </div>
          <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4">
            <div class="text-2xl font-bold text-purple-600 dark:text-purple-400">{{ syncDashboard.stats?.mailbox_contacts || 0 }}</div>
            <div class="text-sm text-slate-500 dark:text-slate-400">Mailbox Kontakte</div>
          </div>
          <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4">
            <div class="text-2xl font-bold text-green-600 dark:text-green-400">{{ syncDashboard.stats?.cached_photos || 0 }}</div>
            <div class="text-sm text-slate-500 dark:text-slate-400">Gecachte Fotos</div>
          </div>
        </div>

        <!-- Sources Status -->
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <h2 class="text-lg font-semibold text-slate-800 dark:text-white mb-4">Quellen-Status</h2>
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-slate-200 dark:border-slate-700">
                  <th class="text-left py-2 px-3 font-medium text-slate-600 dark:text-slate-400">Quelle</th>
                  <th class="text-left py-2 px-3 font-medium text-slate-600 dark:text-slate-400">Status</th>
                  <th class="text-left py-2 px-3 font-medium text-slate-600 dark:text-slate-400">Kontakte</th>
                  <th class="text-left py-2 px-3 font-medium text-slate-600 dark:text-slate-400">Letzter Sync</th>
                  <th class="text-left py-2 px-3 font-medium text-slate-600 dark:text-slate-400">Syncs</th>
                  <th class="text-left py-2 px-3 font-medium text-slate-600 dark:text-slate-400">Fehler</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="source in syncDashboard.sources" :key="source.id" class="border-b border-slate-100 dark:border-slate-700/50">
                  <td class="py-2 px-3 text-slate-800 dark:text-slate-200">{{ source.display_name }}</td>
                  <td class="py-2 px-3">
                    <span 
                      class="px-2 py-0.5 text-xs rounded-full"
                      :class="source.last_sync_status === 'success' 
                        ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300' 
                        : source.last_sync_status === 'error'
                        ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'"
                    >
                      {{ source.last_sync_status || 'Nie' }}
                    </span>
                  </td>
                  <td class="py-2 px-3 text-slate-600 dark:text-slate-400">{{ source.contact_count }}</td>
                  <td class="py-2 px-3 text-slate-600 dark:text-slate-400">{{ source.last_sync_at ? formatDate(source.last_sync_at) : '-' }}</td>
                  <td class="py-2 px-3 text-slate-600 dark:text-slate-400">{{ source.total_syncs }}</td>
                  <td class="py-2 px-3">
                    <span v-if="source.error_count > 0" class="text-red-600 dark:text-red-400 font-medium">{{ source.error_count }}</span>
                    <span v-else class="text-slate-400">0</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Recent Syncs -->
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <h2 class="text-lg font-semibold text-slate-800 dark:text-white mb-4">Letzte Synchronisierungen</h2>
          <div class="space-y-2 max-h-80 overflow-y-auto">
            <div v-if="syncDashboard.recentSyncs?.length === 0" class="text-center text-slate-400 dark:text-slate-500 py-8">
              Keine Sync-Historie vorhanden
            </div>
            <div
              v-for="sync in syncDashboard.recentSyncs"
              :key="sync.id"
              class="flex items-center gap-3 p-3 rounded-lg border border-slate-100 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800/50"
            >
              <span
                class="w-2 h-2 rounded-full flex-shrink-0"
                :class="sync.status === 'success' ? 'bg-green-500' : sync.status === 'error' ? 'bg-red-500' : 'bg-amber-500'"
              ></span>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="font-medium text-slate-800 dark:text-slate-200">{{ sync.source_name }}</span>
                  <span class="text-xs text-slate-400">{{ formatDate(sync.started_at) }}</span>
                </div>
                <div class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  <span v-if="sync.status === 'success'">
                    +{{ sync.contacts_added }} neu, {{ sync.contacts_updated }} aktualisiert, -{{ sync.contacts_removed }} entfernt
                  </span>
                  <span v-else-if="sync.status === 'error'" class="text-red-500">{{ sync.error_message }}</span>
                  <span v-else>In Bearbeitung...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Call Queues Tab -->
      <template v-if="activeTab === 'callqueues'">
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-slate-800 dark:text-white">Call Queue Mitglieder</h2>
            <button
              @click="fetchCallQueues"
              class="px-3 py-1.5 text-slate-600 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg flex items-center gap-1 text-sm transition-colors"
            >
              <RefreshCw class="w-4 h-4" :class="{ 'animate-spin': callQueuesLoading }" />
              Aktualisieren
            </button>
          </div>

          <div v-if="callQueuesLoading" class="text-center py-8">
            <RefreshCw class="w-6 h-6 mx-auto text-slate-400 animate-spin" />
            <p class="text-sm text-slate-500 mt-2">Lade Call Queues...</p>
          </div>

          <div v-else-if="callQueues.length === 0" class="text-center text-slate-400 dark:text-slate-500 py-8">
            <Users class="w-8 h-8 mx-auto mb-2" />
            Noch keine Call Queue Mitglieder erkannt
          </div>

          <div v-else class="space-y-6">
            <div v-for="queue in callQueues" :key="queue.queue_user_id" class="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <!-- Queue Header -->
              <div class="bg-slate-50 dark:bg-slate-700/50 px-4 py-3 flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                    <Phone class="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <div class="font-medium text-slate-800 dark:text-white">{{ queue.queue_name }}</div>
                    <div class="text-xs text-slate-500 dark:text-slate-400">{{ queue.members.length }} Mitglieder</div>
                  </div>
                </div>
                <button
                  @click="toggleAddMemberForm(queue.queue_user_id)"
                  class="px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg flex items-center gap-1 transition-colors"
                >
                  <UserPlus class="w-4 h-4" />
                  Hinzufügen
                </button>
              </div>

              <!-- Add Member Form (Expandable) -->
              <div v-if="addMemberFormOpen === queue.queue_user_id" class="px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-slate-200 dark:border-slate-700">
                <div class="flex gap-2">
                  <div class="relative flex-1">
                    <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      v-model="memberSearchQuery"
                      type="text"
                      placeholder="Mitarbeiter suchen..."
                      class="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-800 dark:text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      @input="debouncedSearchMembers"
                    >
                    <!-- Search Results Dropdown -->
                    <div v-if="memberSearchResults.length > 0" class="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                      <button
                        v-for="contact in memberSearchResults"
                        :key="contact.email"
                        @click="addMemberToQueue(queue.queue_user_id, queue.queue_name, contact)"
                        class="w-full px-4 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-700 text-sm flex items-center gap-3"
                      >
                        <div class="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-xs font-medium text-slate-600 dark:text-slate-300">
                          {{ getInitials(contact.display_name) }}
                        </div>
                        <div>
                          <div class="font-medium text-slate-800 dark:text-white">{{ contact.display_name }}</div>
                          <div class="text-xs text-slate-500 dark:text-slate-400">{{ contact.email }}</div>
                        </div>
                      </button>
                    </div>
                  </div>
                  <button
                    @click="addMemberFormOpen = null; memberSearchQuery = ''; memberSearchResults = []"
                    class="px-3 py-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  >
                    <X class="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <!-- Add Member Error (Teams-compatible, no alert()) -->
              <div v-if="addMemberError" class="mx-4 mt-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-700 dark:text-red-400 flex items-center justify-between">
                <span>{{ addMemberError }}</span>
                <button @click="addMemberError = null" class="ml-2 text-red-400 hover:text-red-600">
                  <X class="w-4 h-4" />
                </button>
              </div>

              <!-- Members List -->
              <div class="divide-y divide-slate-100 dark:divide-slate-700">
                <div
                  v-for="member in queue.members"
                  :key="member.id"
                  class="px-4 py-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/30"
                >
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-xs font-medium text-slate-600 dark:text-slate-300">
                      {{ getInitials(member.member_name) }}
                    </div>
                    <div>
                      <div class="font-medium text-slate-800 dark:text-white">{{ member.member_name }}</div>
                      <div class="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <span>{{ member.call_count }} Anruf{{ member.call_count !== 1 ? 'e' : '' }}</span>
                        <span v-if="member.manually_added" class="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded text-[10px] font-medium">
                          Manuell
                        </span>
                        <span v-else class="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 rounded text-[10px] font-medium">
                          Auto-gelernt
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    @click="askRemoveMember(member.id, member.member_name, queue.queue_name)"
                    class="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    title="Mitglied entfernen"
                  >
                    <Trash2 class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <p class="text-sm text-slate-500 dark:text-slate-400">
              <strong>Auto-gelernt:</strong> Mitglieder werden automatisch erkannt wenn sie Call Queue Anrufe annehmen.<br>
              <strong>Manuell:</strong> Von Admin hinzugefügte Mitglieder (z.B. vor dem ersten Anruf).
            </p>
          </div>
        </div>
      </template>

      <!-- Duplicates Tab -->
      <template v-if="activeTab === 'duplicates'">
        <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-semibold text-slate-800 dark:text-white">Potenzielle Duplikate</h2>
            <div class="flex gap-2">
              <button
                @click="duplicateType = 'name'; fetchDuplicates()"
                class="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
                :class="duplicateType === 'name' 
                  ? 'bg-slate-700 text-white' 
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'"
              >
                Nach Name
              </button>
              <button
                @click="duplicateType = 'phone'; fetchDuplicates()"
                class="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
                :class="duplicateType === 'phone' 
                  ? 'bg-slate-700 text-white' 
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'"
              >
                Nach Telefon
              </button>
            </div>
          </div>

          <div v-if="duplicatesLoading" class="text-center py-8">
            <RefreshCw class="w-6 h-6 mx-auto text-slate-400 animate-spin" />
            <p class="text-sm text-slate-500 mt-2">Suche Duplikate...</p>
          </div>

          <div v-else-if="duplicates.length === 0" class="text-center text-slate-400 dark:text-slate-500 py-8">
            <CheckCircle class="w-8 h-8 mx-auto mb-2 text-green-500" />
            Keine Duplikate gefunden
          </div>

          <div v-else class="space-y-3 max-h-[500px] overflow-y-auto">
            <div
              v-for="(dup, index) in duplicates"
              :key="index"
              class="flex items-center gap-4 p-4 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20"
            >
              <!-- Contact 1 -->
              <div class="flex-1 min-w-0">
                <div class="font-medium text-slate-800 dark:text-slate-200 truncate">{{ dup.name1 }}</div>
                <div class="text-xs text-slate-500 dark:text-slate-400 truncate">{{ dup.company1 || 'Keine Firma' }}</div>
                <div class="text-xs text-slate-400 truncate">{{ dup.email1 }}</div>
              </div>
              
              <!-- Similarity -->
              <div class="flex-shrink-0 text-center px-3">
                <div v-if="dup.similarity_score" class="text-lg font-bold text-amber-600 dark:text-amber-400">
                  {{ Math.round(dup.similarity_score * 100) }}%
                </div>
                <div v-else class="text-xs text-slate-500">
                  <Phone class="w-4 h-4 mx-auto" />
                  {{ dup.phone }}
                </div>
                <div class="text-xs text-slate-400">Ähnlichkeit</div>
              </div>
              
              <!-- Contact 2 -->
              <div class="flex-1 min-w-0 text-right">
                <div class="font-medium text-slate-800 dark:text-slate-200 truncate">{{ dup.name2 }}</div>
                <div class="text-xs text-slate-500 dark:text-slate-400 truncate">{{ dup.company2 || 'Keine Firma' }}</div>
                <div class="text-xs text-slate-400 truncate">{{ dup.email2 }}</div>
              </div>
            </div>
          </div>

          <div v-if="duplicates.length > 0" class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <p class="text-sm text-slate-500 dark:text-slate-400">
              {{ duplicates.length }} potenzielle Duplikate gefunden. 
              Bitte manuell prüfen und ggf. in der Quelle bereinigen.
            </p>
          </div>
        </div>
      </template>

      <!-- Add Source Modal -->
      <div v-if="showAddSource" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60">
        <div class="bg-white dark:bg-slate-800 shadow-xl max-w-md w-full p-6 rounded-xl border border-slate-200 dark:border-slate-700">
          <h3 class="text-lg font-semibold text-slate-800 dark:text-white mb-4">Neue Quelle hinzufügen</h3>
          
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Typ</label>
              <select v-model="newSource.source_type" class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200">
                <option value="shared_mailbox">Shared Mailbox Kontakte</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Anzeigename</label>
              <input
                v-model="newSource.display_name"
                type="text"
                placeholder="z.B. Kundenkontakte"
                class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 placeholder-slate-400"
              >
            </div>

            <div v-if="newSource.source_type === 'shared_mailbox'">
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Mailbox E-Mail
              </label>
              <input
                v-model="newSource.source_identifier"
                type="email"
                placeholder="kontakte@firma.de"
                class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 placeholder-slate-400"
              >
            </div>
          </div>

          <div class="flex justify-end gap-2 mt-6">
            <button
              @click="showAddSource = false"
              class="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white rounded-lg transition-colors"
            >
              Abbrechen
            </button>
            <button
              @click="addSource"
              class="px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg transition-colors"
            >
              Hinzufügen
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Clear Logs Confirmation Modal -->
    <div
      v-if="showClearLogsConfirm"
      class="fixed inset-0 z-[60] flex items-center justify-center p-4"
      @click.self="showClearLogsConfirm = false"
    >
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
      <div class="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-sm w-full p-6">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
            <Trash2 class="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Logs löschen?</h3>
        </div>
        <p class="text-slate-600 dark:text-slate-400 mb-6">
          Möchtest du wirklich <strong class="text-slate-900 dark:text-slate-100">alle Logs</strong> löschen? Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        <div class="flex gap-3 justify-end">
          <button
            @click="showClearLogsConfirm = false"
            class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
          >
            Abbrechen
          </button>
          <button
            @click="confirmClearLogs"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            Alle löschen
          </button>
        </div>
      </div>
    </div>

    <!-- Delete CQ Member Confirmation Modal -->
    <div
      v-if="showDeleteMemberConfirm"
      class="fixed inset-0 z-[60] flex items-center justify-center p-4"
      @click.self="showDeleteMemberConfirm = false; memberToDelete = null"
    >
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
      <div class="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-sm w-full p-6">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
            <Trash2 class="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Mitglied entfernen?</h3>
        </div>
        <p class="text-slate-600 dark:text-slate-400 mb-6">
          Möchtest du <strong class="text-slate-900 dark:text-slate-100">{{ memberToDelete?.name }}</strong> aus <strong class="text-slate-900 dark:text-slate-100">{{ memberToDelete?.queue }}</strong> entfernen?
        </p>
        <div class="flex gap-3 justify-end">
          <button
            @click="showDeleteMemberConfirm = false; memberToDelete = null"
            class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
          >
            Abbrechen
          </button>
          <button
            @click="confirmRemoveMember"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            Entfernen
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Source Confirmation Modal -->
    <div
      v-if="showDeleteSourceConfirm"
      class="fixed inset-0 z-[60] flex items-center justify-center p-4"
      @click.self="showDeleteSourceConfirm = false; sourceToDelete = null"
    >
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
      <div class="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-sm w-full p-6">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
            <Trash2 class="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">Quelle löschen?</h3>
        </div>
        <p class="text-slate-600 dark:text-slate-400 mb-6">
          Möchtest du die Quelle <strong class="text-slate-900 dark:text-slate-100">{{ sourceToDelete?.display_name }}</strong> wirklich löschen?
        </p>
        <div class="flex gap-3 justify-end">
          <button
            @click="showDeleteSourceConfirm = false; sourceToDelete = null"
            class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
          >
            Abbrechen
          </button>
          <button
            @click="confirmDeleteSource"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            Löschen
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { X, RefreshCw, Plus, Trash2, Search, Phone, PhoneCall, CheckCircle, UserPlus, Users, Info, AlertCircle, Download, Clock, ToggleLeft, ToggleRight, History } from 'lucide-vue-next';
import { authenticatedFetch } from '../oauth2-config';

defineEmits(['close']);

const API_URL = process.env.VUE_APP_API_URL || '__VUE_APP_API_URL__';

// Sources Tab
const activeTab = ref('sources');
const sources = ref([]);
const syncing = ref(false);
const showAddSource = ref(false);
const newSource = ref({
  source_type: 'shared_mailbox',
  display_name: '',
  source_identifier: ''
});

// Sync Dashboard Tab
const syncDashboard = ref({
  sources: [],
  recentSyncs: [],
  stats: {}
});

// Duplicates Tab
const duplicates = ref([]);
const duplicatesLoading = ref(false);
const duplicateType = ref('name');

// Call Queues Tab
const callQueues = ref([]);
const callQueuesLoading = ref(false);
const addMemberFormOpen = ref(null);
const memberSearchQuery = ref('');
const memberSearchResults = ref([]);
const addMemberError = ref(null);
let memberSearchTimeout = null;

// Logs Tab
const logs = ref([]);
const logsLoading = ref(false);
const logSearch = ref('');
const logLevel = ref('');
let searchTimeout = null;

// Debug Tab
const debugSearch = ref('');
const debugLoading = ref(false);
const debugSearched = ref(false);
const debugUser = ref(null);
const debugSearchResults = ref([]);
let debugSearchTimeout = null;

// Maintenance Tab
const repairLoading = ref(false);
const repairResult = ref(null);
const repairOptions = ref({
  limit: 50,
  minDurationSeconds: 30,
  repairTransfers: true
});

// Backfill
const backfillLoading = ref(false);
const backfillResult = ref(null);
const today = new Date().toISOString().split('T')[0];
const backfillOptions = ref({
  startDate: '',
  endDate: today
});

// Auto-Backfill settings & history
const autoBackfillSettings = ref({ auto_enabled: false, run_hour: 1 });
const autoBackfillLoading = ref(false);
const backfillHistory = ref([]);
const backfillHistoryLoading = ref(false);

// Confirmation modals (Teams doesn't support native confirm())
const showClearLogsConfirm = ref(false);
const showDeleteSourceConfirm = ref(false);
const sourceToDelete = ref(null);
const showDeleteMemberConfirm = ref(false);
const memberToDelete = ref(null);

onMounted(async () => {
  await fetchSources();
});

// Sync Dashboard
const fetchSyncDashboard = async () => {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/admin/sources/sync-dashboard`);
    if (response.ok) {
      syncDashboard.value = await response.json();
    }
  } catch (error) {
    console.error('Error fetching sync dashboard:', error);
  }
};

// Duplicates
const fetchDuplicates = async () => {
  duplicatesLoading.value = true;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/admin/sources/duplicates?type=${duplicateType.value}`);
    if (response.ok) {
      duplicates.value = await response.json();
    }
  } catch (error) {
    console.error('Error fetching duplicates:', error);
  } finally {
    duplicatesLoading.value = false;
  }
};

// Maintenance Tab - Repair calls
const runRepair = async (dryRun = false) => {
  repairLoading.value = true;
  repairResult.value = null;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/admin/calls/repair`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        limit: repairOptions.value.limit,
        minDurationSeconds: repairOptions.value.minDurationSeconds,
        repairTransfers: repairOptions.value.repairTransfers,
        dryRun
      })
    });
    if (response.ok) {
      repairResult.value = await response.json();
    } else {
      const error = await response.text();
      repairResult.value = { success: false, error };
    }
  } catch (error) {
    console.error('Error running repair:', error);
    repairResult.value = { success: false, error: error.message };
  } finally {
    repairLoading.value = false;
  }
};

// Backfill missing call records from Graph API
const runBackfill = async (dryRun = false) => {
  backfillLoading.value = true;
  backfillResult.value = null;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/admin/calls/backfill`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        startDate: backfillOptions.value.startDate,
        endDate: backfillOptions.value.endDate || new Date().toISOString(),
        dryRun
      })
    });
    if (response.ok) {
      backfillResult.value = await response.json();
      // Refresh history after successful backfill
      fetchBackfillHistory();
    } else {
      const error = await response.json();
      backfillResult.value = { success: false, error: error.details || error.error };
    }
  } catch (error) {
    console.error('Error running backfill:', error);
    backfillResult.value = { success: false, error: error.message };
  } finally {
    backfillLoading.value = false;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString('de-DE', { 
    day: '2-digit', 
    month: '2-digit', 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

const formatDateTimeFull = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Auto-Backfill settings
const fetchBackfillSettings = async () => {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/admin/calls/backfill/settings`);
    if (response.ok) {
      autoBackfillSettings.value = await response.json();
    }
  } catch (error) {
    console.error('Error fetching backfill settings:', error);
  }
};

const toggleAutoBackfill = async () => {
  autoBackfillLoading.value = true;
  try {
    const newState = !autoBackfillSettings.value.auto_enabled;
    const response = await authenticatedFetch(`${API_URL}/api/admin/calls/backfill/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        auto_enabled: newState,
        run_hour: autoBackfillSettings.value.run_hour
      })
    });
    if (response.ok) {
      autoBackfillSettings.value = await response.json();
    }
  } catch (error) {
    console.error('Error toggling auto-backfill:', error);
  } finally {
    autoBackfillLoading.value = false;
  }
};

const saveAutoBackfillSettings = async () => {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/admin/calls/backfill/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        auto_enabled: autoBackfillSettings.value.auto_enabled,
        run_hour: autoBackfillSettings.value.run_hour
      })
    });
    if (response.ok) {
      autoBackfillSettings.value = await response.json();
    }
  } catch (error) {
    console.error('Error saving backfill settings:', error);
  }
};

const fetchBackfillHistory = async () => {
  backfillHistoryLoading.value = true;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/admin/calls/backfill/history?limit=30`);
    if (response.ok) {
      backfillHistory.value = await response.json();
    }
  } catch (error) {
    console.error('Error fetching backfill history:', error);
  } finally {
    backfillHistoryLoading.value = false;
  }
};

// Call Queues
const fetchCallQueues = async () => {
  callQueuesLoading.value = true;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/admin/call-queues`);
    if (response.ok) {
      callQueues.value = await response.json();
    }
  } catch (error) {
    console.error('Error fetching call queues:', error);
  } finally {
    callQueuesLoading.value = false;
  }
};

const toggleAddMemberForm = (queueId) => {
  if (addMemberFormOpen.value === queueId) {
    addMemberFormOpen.value = null;
    memberSearchQuery.value = '';
    memberSearchResults.value = [];
  } else {
    addMemberFormOpen.value = queueId;
    memberSearchQuery.value = '';
    memberSearchResults.value = [];
  }
};

const debouncedSearchMembers = () => {
  clearTimeout(memberSearchTimeout);
  memberSearchTimeout = setTimeout(searchAvailableMembers, 300);
};

const searchAvailableMembers = async () => {
  if (!memberSearchQuery.value || memberSearchQuery.value.length < 2) {
    memberSearchResults.value = [];
    return;
  }
  try {
    const response = await authenticatedFetch(`${API_URL}/api/admin/call-queues/available-members?search=${encodeURIComponent(memberSearchQuery.value)}`);
    if (response.ok) {
      memberSearchResults.value = await response.json();
    }
  } catch (error) {
    console.error('Error searching members:', error);
  }
};

const addMemberToQueue = async (queueUserId, queueName, contact) => {
  addMemberError.value = null;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/admin/call-queues/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        queue_user_id: queueUserId,
        queue_name: queueName,
        member_user_id: contact.entra_id,
        member_name: contact.display_name
      })
    });
    if (response.ok) {
      addMemberFormOpen.value = null;
      memberSearchQuery.value = '';
      memberSearchResults.value = [];
      await fetchCallQueues();
    } else {
      const err = await response.json();
      addMemberError.value = err.error || 'Fehler beim Hinzufügen';
      setTimeout(() => { addMemberError.value = null; }, 5000);
    }
  } catch (error) {
    console.error('Error adding member:', error);
    addMemberError.value = 'Fehler beim Hinzufügen des Mitglieds';
    setTimeout(() => { addMemberError.value = null; }, 5000);
  }
};

const askRemoveMember = (memberId, memberName, queueName) => {
  memberToDelete.value = { id: memberId, name: memberName, queue: queueName };
  showDeleteMemberConfirm.value = true;
};

const confirmRemoveMember = async () => {
  if (!memberToDelete.value) return;
  const { id } = memberToDelete.value;
  showDeleteMemberConfirm.value = false;
  try {
    const response = await authenticatedFetch(`${API_URL}/api/admin/call-queues/members/${id}`, {
      method: 'DELETE'
    });
    if (response.ok) {
      await fetchCallQueues();
    }
  } catch (error) {
    console.error('Error removing member:', error);
  } finally {
    memberToDelete.value = null;
  }
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const fetchSources = async () => {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/admin/sources`);
    if (response.ok) {
      sources.value = await response.json();
    }
  } catch (error) {
    console.error('Error fetching sources:', error);
  }
};

const fetchLogs = async () => {
  logsLoading.value = true;
  try {
    const params = new URLSearchParams();
    if (logSearch.value) params.set('search', logSearch.value);
    if (logLevel.value) params.set('level', logLevel.value);
    params.set('limit', '100');
    
    const response = await authenticatedFetch(`${API_URL}/api/admin/logs?${params}`);
    if (response.ok) {
      logs.value = await response.json();
    }
  } catch (error) {
    console.error('Error fetching logs:', error);
  } finally {
    logsLoading.value = false;
  }
};

const debouncedFetchLogs = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(fetchLogs, 300);
};

// Show clear logs confirmation modal
const askClearLogs = () => {
  showClearLogsConfirm.value = true;
};

// Confirm and clear logs
const confirmClearLogs = async () => {
  showClearLogsConfirm.value = false;
  try {
    await authenticatedFetch(`${API_URL}/api/admin/logs`, { method: 'DELETE' });
    logs.value = [];
  } catch (error) {
    console.error('Error clearing logs:', error);
  }
};

const formatLogDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('de-DE', { 
    day: '2-digit', 
    month: '2-digit', 
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit' 
  });
};

// Debug Tab Functions
const debouncedDebugSearch = () => {
  clearTimeout(debugSearchTimeout);
  debugSearchTimeout = setTimeout(() => {
    if (debugSearch.value.length >= 2) {
      searchDebugUser();
    }
  }, 500);
};

const searchDebugUser = async () => {
  if (!debugSearch.value.trim()) return;
  
  debugLoading.value = true;
  debugSearched.value = true;
  debugUser.value = null;
  debugSearchResults.value = [];
  
  try {
    const response = await authenticatedFetch(
      `${API_URL}/api/admin/call-queues/debug/user?search=${encodeURIComponent(debugSearch.value)}`
    );
    if (response.ok) {
      const data = await response.json();
      if (data.contacts) {
        // Multiple results - show list
        debugSearchResults.value = data.contacts;
        // If only one result, auto-select it
        if (data.contacts.length === 1) {
          await selectDebugContact(data.contacts[0].email);
        }
      } else if (data.contact) {
        // Single result (legacy format)
        debugUser.value = data;
      }
    } else if (response.status === 404) {
      debugSearchResults.value = [];
    }
  } catch (error) {
    console.error('Error searching debug user:', error);
    debugSearchResults.value = [];
  } finally {
    debugLoading.value = false;
  }
};

// Select a specific contact from search results to load full details
const selectDebugContact = async (email) => {
  debugLoading.value = true;
  try {
    const response = await authenticatedFetch(
      `${API_URL}/api/admin/call-queues/debug/user?email=${encodeURIComponent(email)}`
    );
    if (response.ok) {
      debugUser.value = await response.json();
    }
  } catch (error) {
    console.error('Error loading debug contact details:', error);
  } finally {
    debugLoading.value = false;
  }
};

const formatDebugBusinessPhones = (phones) => {
  if (!phones || !Array.isArray(phones) || phones.length === 0) return '-';
  return phones.join(', ');
};

const getDebugPresenceClass = (availability) => {
  const classes = {
    'Available': 'bg-green-500',
    'Busy': 'bg-red-500',
    'DoNotDisturb': 'bg-red-600',
    'Away': 'bg-yellow-500',
    'BeRightBack': 'bg-yellow-400',
    'Offline': 'bg-gray-400',
    'PresenceUnknown': 'bg-gray-300'
  };
  return classes[availability] || 'bg-gray-300';
};

const formatDebugCallDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const syncAll = async () => {
  syncing.value = true;
  try {
    await authenticatedFetch(`${API_URL}/api/admin/sync-all`, { method: 'POST' });
    setTimeout(() => {
      fetchSources();
      syncing.value = false;
    }, 2000);
  } catch (error) {
    console.error('Error syncing all:', error);
    syncing.value = false;
  }
};

const syncSource = async (id) => {
  try {
    await authenticatedFetch(`${API_URL}/api/admin/sources/${id}/sync`, { method: 'POST' });
    setTimeout(fetchSources, 2000);
  } catch (error) {
    console.error('Error syncing source:', error);
  }
};

const addSource = async () => {
  try {
    const response = await authenticatedFetch(`${API_URL}/api/admin/sources`, {
      method: 'POST',
      body: JSON.stringify(newSource.value)
    });
    if (response.ok) {
      showAddSource.value = false;
      newSource.value = { source_type: 'shared_mailbox', display_name: '', source_identifier: '' };
      fetchSources();
    }
  } catch (error) {
    console.error('Error adding source:', error);
  }
};

// Ask to delete source (show modal)
const askDeleteSource = (source) => {
  sourceToDelete.value = source;
  showDeleteSourceConfirm.value = true;
};

// Confirm and delete source
const confirmDeleteSource = async () => {
  if (!sourceToDelete.value) return;
  const id = sourceToDelete.value.id;
  showDeleteSourceConfirm.value = false;
  sourceToDelete.value = null;
  
  try {
    await authenticatedFetch(`${API_URL}/api/admin/sources/${id}`, { method: 'DELETE' });
    fetchSources();
  } catch (error) {
    console.error('Error deleting source:', error);
  }
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('de-DE');
};
</script>
